import React from "react";
import { FIRM } from "../lib/firm";

const money = (n) => "USD " + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const longDate = (iso) => {
  if (!iso) return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const d = (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) ? new Date(iso + "T00:00:00") : new Date(iso);
  return isNaN(d) ? String(iso || "") : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/**
 * One Payoff Direction & Release letter PER existing lender, so each lien on a
 * consolidated deal gets its own full payoff instrument. Renders nothing when
 * there are no captured mortgages.
 */
export default function LenderPayoffSet({ mortgages, borrowerName, property, financier, closingDate, debtType }) {
  const liens = Array.isArray(mortgages) ? mortgages.filter((m) => m && (m.lender || m.balance)) : [];
  if (liens.length === 0) return null;
  const when = longDate(closingDate);
  const _dt = (debtType || "").toLowerCase();
  const _NOUN = { mortgage: "Mortgage", auto: "Auto Loan", auto_loan: "Auto Loan", student_loan: "Student Loan", student: "Student Loan", credit_card: "Credit Card", personal: "Personal/Consumer Loan", consumer: "Personal/Consumer Loan", other: "Consumer Loan" };
  const isMortgage = _dt ? _dt === "mortgage" : true;
  const noun = _NOUN[_dt] || "Obligation";
  const releaseClause = isMortgage
    ? "the lender/servicer is directed to prepare, execute, and record the appropriate release, satisfaction, or reconveyance of its security interest in the Property in accordance with applicable state and local recording requirements."
    : "the lender/servicer is directed to release its security interest, mark the account paid-in-full, and issue a written payoff/satisfaction confirmation.";
  const consolidationClause = isMortgage
    ? "This lien is being paid off and consolidated, together with the Borrower's other mortgage(s), into a single new 0% note held by "
    : "This account is being paid off and consolidated, together with the Borrower's other obligation(s), into a single new 0% note held by ";
  return (
    <>
      {liens.map((m, i) => (
        <div className="paper-sheet legal-page" key={i} data-testid={`binder-lender-payoff-${i}`}>
          <div className="lg-eyebrow">Payoff Instruction · {isMortgage ? "Lien" : "Account"} {i + 1} of {liens.length}</div>
          <h1 className="lg-title">Payoff Direction &amp; Authorization to Release — {m.position} {noun}</h1>
          <p className="lg-meta">Date: {when} · Financier: {financier || FIRM.name}</p>

          <table className="lg-kv">
            <tbody>
              <tr><td>Lender / Servicer</td><td>{m.lender || "—"}</td></tr>
              <tr><td>Lender Address</td><td>{m.address || "—"}</td></tr>
              <tr><td>Loan / Account No.</td><td>{m.account || "—"}</td></tr>
              <tr><td>Borrower / Obligor</td><td>{borrowerName || "—"}</td></tr>
              <tr><td>{isMortgage ? "Property / Collateral" : "Collateral / Reference"}</td><td>{property || "—"}</td></tr>
              <tr><td>{isMortgage ? "Lien Position" : "Priority"}</td><td>{m.position}</td></tr>
              <tr><td>Payoff Amount (this {isMortgage ? "lien" : "account"})</td><td>{money(m.balance)}</td></tr>
            </tbody>
          </table>

          <ol className="lg-clauses">
            <li><span className="lg-ct">Payoff Direction.</span> The Borrower irrevocably directs and authorizes <strong>{m.lender || "the lender/servicer"}</strong> (Acct <strong>{m.account || "—"}</strong>) to accept payoff funds tendered by <strong>{financier || FIRM.name}</strong> and to apply them to satisfy the above-referenced obligation in full.</li>
            <li><span className="lg-ct">Release of {isMortgage ? "Lien" : "Security Interest"}.</span> Upon receipt of the payoff funds, {releaseClause}</li>
            <li><span className="lg-ct">Authorization to Release Information.</span> The Borrower authorizes the lender/servicer to disclose the payoff statement, per-diem, and loan information to {financier || FIRM.name} and its authorized agents until this obligation is satisfied and released.</li>
            <li><span className="lg-ct">Consolidation Notice.</span> {consolidationClause}{financier || FIRM.name}.</li>
          </ol>

          <div className="lg-sigrow" style={{ marginTop: 28 }}>
            <div className="lg-sigblock">
              <div className="lg-sigline">{borrowerName || "Borrower"}</div>
              <div className="lg-sigcap">Borrower / Obligor</div>
            </div>
            <div className="lg-sigblock">
              <div className="lg-sigline">{financier || FIRM.name}</div>
              <div className="lg-sigcap">Financier</div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
